import type { FailedSaleTransaction } from '~/api/graphql/generated/types'
import i18n from '~/locales/i18n'
/**
 * Transaction Failure Reason IDs from backend enum TransactionFailureReason
 * Maps to static_transaction_failure_reason table
 */
export enum TransactionFailureReasonId {
  DEVICE_OFFLINE = 1,
  DEVICE_NOT_FOUND = 2,
  DEVICE_TIMEOUT = 3,
  DEVICE_BUSY = 4,
  DEVICE_BATTERY_LOW = 5,
  CANCELLED_BY_DEVICE = 6,
  CANCELLED_BY_API = 7,
  PROCESSOR_DECLINED = 8,
  GATEWAY_REJECTED = 9,
  SETTLEMENT_DECLINED = 10,
  UNKNOWN = 11,
}

/**
 * Error categories for grouping similar error types
 */
export enum PaymentErrorCategory {
  CONNECTIVITY = 'connectivity',
  ISSUER_DECLINE = 'issuer_decline',
  UNKNOWN = 'unknown',
}

/**
 * Maps transaction failure reason ID to error category
 */
const FAILURE_REASON_TO_CATEGORY_MAP: Record<
  TransactionFailureReasonId,
  PaymentErrorCategory
> = {
  [TransactionFailureReasonId.DEVICE_OFFLINE]:
    PaymentErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonId.DEVICE_NOT_FOUND]:
    PaymentErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonId.DEVICE_TIMEOUT]:
    PaymentErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonId.DEVICE_BUSY]: PaymentErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonId.DEVICE_BATTERY_LOW]:
    PaymentErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonId.CANCELLED_BY_DEVICE]:
    PaymentErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonId.CANCELLED_BY_API]:
    PaymentErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonId.GATEWAY_REJECTED]:
    PaymentErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonId.PROCESSOR_DECLINED]:
    PaymentErrorCategory.ISSUER_DECLINE,
  [TransactionFailureReasonId.SETTLEMENT_DECLINED]:
    PaymentErrorCategory.ISSUER_DECLINE,
  [TransactionFailureReasonId.UNKNOWN]: PaymentErrorCategory.UNKNOWN,
}

const getFailureReasonCategory = (
  failureReasonId: number,
): PaymentErrorCategory =>
  FAILURE_REASON_TO_CATEGORY_MAP[
    failureReasonId as TransactionFailureReasonId
  ] ?? PaymentErrorCategory.UNKNOWN

/**
 * Gets translated message for error category (without account credits)
 * Translations are computed dynamically, so they update when i18n language changes.
 */
const getCategoryTranslationMessage = (
  category: PaymentErrorCategory,
): string => {
  switch (category) {
    case PaymentErrorCategory.ISSUER_DECLINE:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_ISSUER_DECLINE',
      )
    case PaymentErrorCategory.CONNECTIVITY:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY',
      )
    case PaymentErrorCategory.UNKNOWN:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY',
      )
    default:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY',
      )
  }
}

/**
 * Gets translated message for error category (with account credits applied)
 * Translations are computed dynamically, so they update when i18n language changes.
 */
const getCategoryTranslationMessageWithCredits = (
  category: PaymentErrorCategory,
): string => {
  switch (category) {
    case PaymentErrorCategory.ISSUER_DECLINE:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_ISSUER_DECLINE',
      )
    case PaymentErrorCategory.CONNECTIVITY:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY_WITH_CREDITS',
      )
    case PaymentErrorCategory.UNKNOWN:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY_WITH_CREDITS',
      )
    default:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY_WITH_CREDITS',
      )
  }
}

const getTransactionFailureTranslationMessage = (
  failureReasonId: number,
  accountCreditsApplied: boolean,
): string => {
  const category = getFailureReasonCategory(failureReasonId)

  try {
    return accountCreditsApplied
      ? getCategoryTranslationMessageWithCredits(category)
      : getCategoryTranslationMessage(category)
  } catch {
    // Fallback to connectivity message if translation fails
    return accountCreditsApplied
      ? getCategoryTranslationMessageWithCredits(
          PaymentErrorCategory.CONNECTIVITY,
        )
      : getCategoryTranslationMessage(PaymentErrorCategory.CONNECTIVITY)
  }
}

/**
 * Extracts failure reason ID from FailedSaleTransaction
 * ConstantEntity.id is a GraphQL ID type (string), but contains numeric values
 */
const extractFailureReasonId = (
  transaction: FailedSaleTransaction,
): number | null => {
  const failureReason = transaction.error?.failureReason

  if (!failureReason) {
    return null
  }
  const { id } = failureReason

  if (typeof id === 'string') {
    const parsedId = parseInt(id, 10)
    return Number.isNaN(parsedId) ? null : parsedId
  }
  if (typeof id === 'number') {
    return id
  }

  return null
}

/**
 * Maps a FailedSaleTransaction to a user-friendly error message
 */
export const getTransactionFailureMessage = (
  transaction: FailedSaleTransaction,
  accountCreditsApplied?: boolean,
): string => {
  const failureReasonId = extractFailureReasonId(transaction)

  if (failureReasonId === null) {
    // Fallback to generic message if we can't determine failure reason
    const fallbackMessage = accountCreditsApplied
      ? i18n.t(
          'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY_WITH_CREDITS',
        )
      : i18n.t('Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY')
    return fallbackMessage
  }

  return getTransactionFailureTranslationMessage(
    failureReasonId,
    accountCreditsApplied || false,
  )
}

/**
 * Maps account credit application failure to user-friendly error message
 */
export const getAccountCreditFailureMessage = (errorReason: string): string =>
  i18n.t('Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.ACCOUNT_CREDIT_FAILURE', {
    reason: errorReason,
  })

/**
 * Maps terminal initiation failure to user-friendly error message
 */
export const getTerminalInitiationFailureMessage = (
  accountCreditsApplied?: boolean,
): string => {
  if (accountCreditsApplied) {
    return i18n.t(
      'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TERMINAL_INITIATION_FAILURE_WITH_CREDITS',
    )
  }

  return i18n.t(
    'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TERMINAL_INITIATION_FAILURE',
  )
}
